/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { sub } from 'date-fns';
import { uniqueId } from 'lodash';
import { Plugin } from 'ckeditor5';
import type { RevisionHistory } from 'ckeditor5-premium-features';

export class RevisionHistoryIntegration extends Plugin {
	public static get requires() {
		return [ 'RevisionHistory' ] as const;
	}

	public static get pluginName() {
		return 'RevisionHistoryIntegration' as const;
	}

	public init(): void {
		const editor = this.editor;

		// Store locally the initial data and set it to an empty string in the editor config to prevent
		// the `editor-initial-data-replaced-with-revision-data` warning.
		const initialData = editor.config.get( 'initialData' ) as string;
		editor.config.set( 'initialData', '' );

		this._loadPredefinedRevisionsData();

		editor.on( 'ready', () => this._saveInitialDataAsRevision( initialData ) );
	}

	private async _saveInitialDataAsRevision( initialEditorData: string ): Promise<void> {
		const revisionTrackerPlugin = this.editor.plugins.get( 'RevisionTracker' );

		this.editor.data.set( initialEditorData, { batchType: { isUndoable: false } } );

		await revisionTrackerPlugin.saveRevision( { name: 'Added content' } );
	}

	private _loadPredefinedRevisionsData(): void {
		const revisionHistoryPlugin = this.editor.plugins.get( 'RevisionHistory' ) as RevisionHistory;

		const revisionsData = [
			{
				id: 'initial',
				name: 'Empty revision',
				createdAt: sub( new Date(), { hours: 5, minutes: 17, seconds: 44 } ),
				creatorId: 'user-1',
				authorsIds: [ 'user-1' ],
				attributes: {},
				fromVersion: 1,
				toVersion: 1,
				diffData: {
					main: {
						insertions: JSON.stringify( [
							{
								type: 'c',
								name: 'p',
								attributes: [],
								children: []
							}
						] ),
						deletions: JSON.stringify( [
							{
								type: 'c',
								name: 'p',
								attributes: [],
								children: []
							}
						] )
					}
				}
			},
			{
				id: uniqueId(),
				name: 'Added first paragraph',
				createdAt: sub( new Date(), { hours: 3, minutes: 10, seconds: 12 } ),
				creatorId: 'user-1',
				authorsIds: [ 'user-1' ],
				attributes: {},
				fromVersion: 2,
				toVersion: 2,
				diffData: {
					main: {
						insertions: JSON.stringify( [
							{
								type: 'c',
								name: 'p',
								attributes: [
									[ 'data-revision-end-after', 'insertion:user-1:0' ],
									[ 'data-revision-start-before', 'insertion:user-1:0' ]
								],
								children: [
									'It\'s always hardest to start something from scratch...'
								]
							}
						] ),
						deletions: JSON.stringify( [
							{
								type: 'c',
								name: 'p',
								attributes: [
									[ 'data-revision-end-after', 'deletion:user-1:0' ],
									[ 'data-revision-start-before', 'deletion:user-1:0' ]
								],
								children: []
							}
						] )
					}
				}
			}
		];

		for ( const revision of revisionsData ) {
			revisionHistoryPlugin.addRevisionData( revision );
		}
	}
}
